<script setup lang="ts">
import Logo from 'assets/logos/logo.svg?url'
import SPGroupLogo from 'assets/logos/sp-group-logo.svg?url'
import EdekaLogo from 'assets/logos/edeka-logo.svg?url'
import MuellerLogo from 'assets/logos/mueller-logo.svg?url'
import StroeerLogo from 'assets/logos/stroeer-logo.svg?url'
import MediaMarktSaturnLogo from 'assets/logos/media-markt-saturn-logo.svg?url'
import SeatBackLogo from 'assets/logos/seatback-logo.svg?url'

const props = defineProps({
    trackPosition: {
        type: Number,
        default: 1
    }
})

const route = useRoute()

const logos = computed(() => [
    {
        logo: EdekaLogo,
        alt: 'EDEKA'
    },
    {
        logo: Logo,
        alt: 'Initiative Vermisste Kinder',
        link: 'https://www.initiative-vermisste-kinder.de/'
    },
    {
        logo: SeatBackLogo,
        alt: 'SeatBack',
        link: 'https://www.seatback.de/'
    },
    {
        logo: MediaMarktSaturnLogo,
        alt: 'MediaMarktSaturn',
        link: 'https://www.ceconomy-mms.com/de/ceconomy'
    },
    {
        logo: StroeerLogo,
        alt: 'Ströer',
        link: 'https://www.stroeer.de/'
    },
    {
        logo: SPGroupLogo,
        alt: 'Serviceplan',
        link: 'https://www.house-of-communication.com/de/de.html'
    },
    {
        logo: MuellerLogo,
        alt: 'Müller'
    }
])

const dataAttributes = (alt: string) => ({
    'data-track': 'click',
    'data-track-position': props.trackPosition,
    'data-track-name': alt,
    'data-track-url': route.fullPath
})
</script>

<template>
    <ul class="logo-list">
        <template v-for="(logo, index) in logos" :key="'logo-' + index">
            <li class="logo-list-item">
                <NuxtLink
                    v-if="logo.link"
                    :to="logo.link"
                    :title="logo.alt"
                    rel="nofollow noopener"
                    target="_blank"
                    v-bind="dataAttributes(logo.alt)"
                >
                    <img
                        :src="logo.logo" :data-key="logo.alt.toLowerCase()" :alt="logo.alt" width="60"
                        height="60"
                    >
                </NuxtLink>
                <span v-else>
                    <img
                        :src="logo.logo" :data-key="logo.alt.toLowerCase()" :alt="logo.alt" width="60"
                        height="60"
                    >
                </span>
            </li>
            <li v-if="index === 2" class="logo-list-break" aria-hidden="true" />
        </template>
    </ul>
</template>

<style  lang="scss">
.logo-list {
    position: relative;
    z-index: 2;
    list-style-type: none;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    gap: px2rem(10) px2rem(20);
    margin: px2rem(20) auto auto;
    padding: 0;
    max-width: 500px;
    width: calc(100vw - 60px);

    @media (width >= 440px) {
        gap: px2rem(15) px2rem(25);
    }

    @include bp(large1) {
        gap: px2rem(15) px2rem(30);
        width: auto;
        max-width: 100%;
    }
}

.logo-list-break {
    display: none;

    @include bp(large1) {
        display: block;
        flex-basis: 100%;
        height: 0;
        margin: 0;
        padding: 0;
    }

    @media (width >= 1440px) {
        display: none;
    }
}

.logo-list-item {
    display: flex;
    align-items: center;
    justify-content: center;

    @media (width >= 1440px) {
        &:nth-child(1) { order: 1; } // EDEKA
        &:nth-child(2) { order: 4; } // Vermisste Kinder → Mitte
        &:nth-child(3) { order: 3; } // SeatBack
        &:nth-child(5) { order: 2; } // MediaMarkt
        &:nth-child(6) { order: 5; } // Ströer
        &:nth-child(7) { order: 6; } // Serviceplan
        &:nth-child(8) { order: 7; } // Müller
    }

    @media (hover: hover) {
        &:hover {
            a img {
                transform: scale(1.1);
            }
        }
    }

    a,
    > span {
        display: block;
        width: 100%;
    }

    img {
        width: auto;
        height: 16px;
        max-width: 100%;
        transition: transform 0.4s ease-in-out;

        @media (width >= 440px) {
            height: 19px;
        }

        @include bp(large1) {
            height: 24px;
        }

        &[data-key="seatback"] {
            height: 24px;

            @media (width >= 440px) {
                height: 30px;
            }

            @include bp(large1) {
                height: 38px;
            }
        }

        &[data-key="initiative vermisste kinder"] {
            height: 24px;

            @media (width >= 440px) {
                height: 30px;
            }

            @include bp(large1) {
                height: 38px;
            }
        }

        &[data-key="edeka"] {
            height: 14px;

            @media (width >= 440px) {
                height: 18px;
            }

            @include bp(large1) {
                height: 22px;
            }
        }
    }
}
</style>
